/**
 * CharacterContext - Character state management for Finding Your Voice
 *
 * Manages active character selection, favourite quotes, and character interactions.
 * Stores preferences in localStorage for persistence.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { CHARACTERS as characters } from '../data/characters';

const CharacterContext = createContext(null);

const STORAGE_KEY = 'fyvCharacterState';

export const CharacterProvider = ({ children }) => {
  const [activeCharacter, setActiveCharacter] = useState(null);
  const [characterPreferences, setCharacterPreferences] = useState({
    favouriteQuotes: [],
  });

  // Load state from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setActiveCharacter(parsed.activeCharacter || null);
        setCharacterPreferences(parsed.characterPreferences || { favouriteQuotes: [] });
      }
    } catch (err) {
      console.error('Failed to load character state:', err);
    }
  }, []);

  // Save state to localStorage
  const saveState = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        activeCharacter,
        characterPreferences,
      }));
    } catch (err) {
      console.error('Failed to save character state:', err);
    }
  }, [activeCharacter, characterPreferences]);

  // Auto-save when state changes
  useEffect(() => {
    const timeoutId = setTimeout(saveState, 500);
    return () => clearTimeout(timeoutId);
  }, [saveState]);

  /**
   * Get a random quote from a specific character
   */
  const getCharacterQuote = useCallback((characterId) => {
    const character = characters?.find(c => c.id === characterId);
    if (!character) return null;

    // Character data currently carries a single signature `quote`; support a
    // future `quotes` array without breaking when it doesn't exist yet.
    const pool = character.quotes?.length
      ? character.quotes
      : (character.quote ? [{ text: character.quote }] : []);
    if (pool.length === 0) return null;

    const chosen = pool[Math.floor(Math.random() * pool.length)];
    return {
      ...(typeof chosen === 'string' ? { text: chosen } : chosen),
      characterId: character.id,
      characterName: character.name,
    };
  }, []);

  /**
   * Toggle a quote as favourite
   */
  const toggleFavouriteQuote = useCallback((quoteId) => {
    setCharacterPreferences(prev => {
      const favourites = prev.favouriteQuotes || [];
      if (favourites.includes(quoteId)) {
        return {
          ...prev,
          favouriteQuotes: favourites.filter(id => id !== quoteId),
        };
      }
      return {
        ...prev,
        favouriteQuotes: [...favourites, quoteId],
      };
    });
  }, []);

  const value = useMemo(() => ({
    // State
    activeCharacter,
    characterPreferences,
    characters,

    // Methods
    setActiveCharacter,
    getCharacterQuote,
    toggleFavouriteQuote,
  }), [
    activeCharacter,
    characterPreferences,
    getCharacterQuote,
    toggleFavouriteQuote,
  ]);

  return (
    <CharacterContext.Provider value={value}>
      {children}
    </CharacterContext.Provider>
  );
};

export const useCharacter = () => {
  const context = useContext(CharacterContext);
  if (!context) {
    throw new Error('useCharacter must be used within a CharacterProvider');
  }
  return context;
};

export default CharacterContext;
